import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  Box,
  Button,
  Divider,
  Grid2,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useListAndSearchAllFunds } from "@rentablo/api/apiv1";
import { BarChart, BarChartItem } from "@components/Chart/highcharts";
import { CASHBACK_WIZARD_START, toCurrency } from "@helpers";
import { GainLossTypography } from "@rentablo/components";
import { bicToName, ORDERED_BICS } from "@helpers";
import { useRecommendationDetails } from "../../RecommendationDetailsProvider";

const INVESTMENT_AMOUNT = 10000;

export const CostAndCashbackSection = () => {
  const theme = useTheme();
  const isDownSM = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedBic, setSelectedBic] = useState<string>();
  const { recommendation, fundDetails } = useRecommendationDetails();
  const { data } = useListAndSearchAllFunds({
    isin: [recommendation.fund.isin],
    includeCommissions: true,
    perPage: 10,
  });

  const fund = data?.funds?.[0];

  const chartData = useMemo(
    () =>
      ORDERED_BICS.reduce((acc, bic) => {
        const chartDataRecord = fund?.courtages?.find((x) => x.bic === bic);
        if (!chartDataRecord || !chartDataRecord.portfolioCommission)
          return acc;
        acc.push({
          name: bicToName(bic, true),
          key: bic,
          y: (chartDataRecord?.portfolioCommission ?? 0) * INVESTMENT_AMOUNT,
        });
        return acc;
      }, [] as BarChartItem[]),
    [fund]
  );

  const selectedItem = useMemo(
    () => fund?.courtages?.find((x) => x.bic === selectedBic),
    [fund?.courtages, selectedBic]
  );

  const discount = (selectedItem?.agio ?? 0) * INVESTMENT_AMOUNT;
  const discountText = discount
    ? toCurrency(discount, "EUR", true, {
        maximumFractionDigits: 0,
      })
    : "-";

  const cashbackAmount =
    (selectedItem?.portfolioCommission ?? 0) * INVESTMENT_AMOUNT;
  const cashbackAmountText = cashbackAmount
    ? toCurrency(cashbackAmount, "EUR", true, {
        maximumFractionDigits: 0,
      })
    : "-";
  const issuePremium = fundDetails?.costStatistics?.allInFee;
  const hasCashback = Boolean(fund?.courtages?.length);

  useEffect(() => {
    if (chartData.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- default selection from data
      setSelectedBic(chartData[0].key);
    }
  }, [chartData]);

  if (!hasCashback && !issuePremium) return null;

  return (
    <Box mt={8} pb={isDownSM ? 32 : 14}>
      <Typography variant="body1" mb={4}>
        {t(
          `recommender.detailsModal.cashback.${
            hasCashback ? "cashbackTitle" : "title"
          }`
        )}
      </Typography>
      <Grid2 container rowGap={8}>
        <Grid2
          size={{
            xs: 12,
          }}
        >
          <Typography variant="body3" color="textSecondary">
            {t("recommender.detailsModal.cashback.ter")}
          </Typography>
          <Typography variant="body1">
            {issuePremium
              ? toCurrency(issuePremium, "EUR", true, {
                  maximumFractionDigits: 0,
                })
              : "-"}
          </Typography>
        </Grid2>
        {hasCashback && (
          <>
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Typography variant="body3" color="textSecondary">
                {t("recommender.detailsModal.cashback.amountPercentage")}
              </Typography>
              <GainLossTypography variant="body1" value={cashbackAmount ?? 0}>
                {cashbackAmountText}
              </GainLossTypography>
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Typography variant="body3" color="textSecondary">
                {t("recommender.detailsModal.cashback.premiumDiscount")}
              </Typography>
              <GainLossTypography variant="body1" value={discount}>
                {discountText}
              </GainLossTypography>
            </Grid2>
          </>
        )}
      </Grid2>
      {chartData.length > 0 && (
        <>
          <Divider sx={{ margin: theme.spacing(8, 0) }} />
          <Typography variant="body1" mb={2}>
            {t("recommender.detailsModal.cashback.commision")}
          </Typography>
          <Typography variant="body3" color="textSecondary" mb={6}>
            {t("recommender.detailsModal.cashback.example")}
          </Typography>
          <BarChart
            type="cost"
            height={300}
            chartData={chartData}
            onBarClick={(bic) => setSelectedBic(bic)}
            selectedBar={selectedBic}
          />
          <Box display="flex" justifyContent="flex-end" mt={5}>
            <Button
              variant="tertiary"
              onClick={() => navigate(CASHBACK_WIZARD_START)}
            >
              {t("recommender.detailsModal.cashback.goToCashback")}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
